/**
 * RoadGraph — grafo navegável construído a partir da malha viária (config.ROADS).
 *
 * Os nós são as interseções das vias; as arestas ligam interseções adjacentes
 * ao longo de cada rua. É a base para:
 *   • vagar (random walk no grafo),
 *   • rotear até um edifício (Dijkstra),
 *   • entrar/sair da cidade pelas 3 vias laterais.
 */
import { ROADS, isDrivable } from '../config.js';
import { ENTRY_POINTS } from './cityLayout.js';

// SÓ vias DIRIGÍVEIS: o eixo interno de uma superquadra virou calçada/parque e não
// pode entrar no grafo dos carros (senão eles atravessam o miolo do distrito a pé-firme
// por cima da calçada). Ver config.isDrivable.
// ORDENADAS: o encadeamento de arestas liga nós CONSECUTIVOS, então a lista precisa
// estar em ordem de coordenada — os tocos de acesso entram no fim do array em config.js.
const V = ROADS.vertical.filter(isDrivable).slice().sort((a, b) => a.x - b.x);
const H = ROADS.horizontal.filter(isDrivable).slice().sort((a, b) => a.z - b.z);

/** Existe interseção (i,j) se o cruzamento cai dentro de AMBAS as vias. */
function valid(i, j) {
  const x = V[i].x, z = H[j].z;
  return x >= H[j].xMin && x <= H[j].xMax && z >= V[i].zMin && z <= V[i].zMax;
}

export class RoadGraph {
  constructor() {
    this.nodes = [];
    const idx = new Map(); // "i,j" -> nodeId

    for (let i = 0; i < V.length; i++) {
      for (let j = 0; j < H.length; j++) {
        if (!valid(i, j)) continue;
        const id = this.nodes.length;
        idx.set(i + ',' + j, id);
        this.nodes.push({ id, x: V[i].x, z: H[j].z, nbr: [] });
      }
    }

    // arestas ao longo das horizontais e das verticais (nós consecutivos)
    for (let j = 0; j < H.length; j++) {
      let prev = -1;
      for (let i = 0; i < V.length; i++) {
        const id = idx.get(i + ',' + j);
        if (id == null) continue;
        if (prev !== -1) this._link(prev, id);
        prev = id;
      }
    }
    for (let i = 0; i < V.length; i++) {
      let prev = -1;
      for (let j = 0; j < H.length; j++) {
        const id = idx.get(i + ',' + j);
        if (id == null) continue;
        if (prev !== -1) this._link(prev, id);
        prev = id;
      }
    }

    // pontos de entrada/saída: surge no ponto MAIS DISTANTE da via (junto à borda
    // do mundo, recuado ~8u p/ ficar sobre o asfalto) e percorre a via inteira até
    // a 1ª interseção — fica claro por qual via o avatar está chegando. A saída usa
    // o mesmo ponto distante (some lá no fim da via).
    this.entries = ENTRY_POINTS.map((ep) => {
      const vertical = ep.side === 'top' || ep.side === 'bottom';
      const nodeId = this._edgeNode(ep.side, vertical ? ep.x : ep.z);
      const n = this.nodes[nodeId];
      const out = ep.side === 'left' || ep.side === 'top' ? -1 : 1;
      // ep.x (horizontais) / ep.z (verticais) já estão na borda do mundo
      const far = vertical
        ? { x: n.x, z: ep.z - out * 8 }
        : { x: ep.x - out * 8, z: n.z };
      return {
        side: ep.side,
        nodeId,
        spawn: { x: far.x, z: far.z, heading: ep.heading },
        off: { x: far.x, z: far.z }
      };
    });
  }

  _link(a, b) {
    if (!this.nodes[a].nbr.includes(b)) this.nodes[a].nbr.push(b);
    if (!this.nodes[b].nbr.includes(a)) this.nodes[b].nbr.push(a);
  }

  /** Nó da borda (extremo da via) no lado pedido:
   *  left/right → menor/maior x na linha z=coord; top/bottom → menor/maior z na coluna x=coord. */
  _edgeNode(side, coord) {
    const horiz = side === 'left' || side === 'right';
    let best = -1;
    for (const n of this.nodes) {
      if (horiz ? Math.abs(n.z - coord) > 1 : Math.abs(n.x - coord) > 1) continue;
      if (best === -1) { best = n.id; continue; }
      const b = this.nodes[best];
      const better = horiz
        ? (side === 'left' ? n.x < b.x : n.x > b.x)
        : (side === 'top' ? n.z < b.z : n.z > b.z);
      if (better) best = n.id;
    }
    return best;
  }

  node(id) {
    return this.nodes[id];
  }

  /** Nó mais próximo de um ponto do mundo. */
  nearestNode(x, z) {
    let best = -1, bd = Infinity;
    for (const n of this.nodes) {
      const d = (n.x - x) ** 2 + (n.z - z) ** 2;
      if (d < bd) { bd = d; best = n.id; }
    }
    return best;
  }

  /** Vizinho aleatório (evita voltar por onde veio, salvo becos sem saída). */
  randomStep(nodeId, prevId, rnd) {
    const nbr = this.nodes[nodeId].nbr;
    const opts = nbr.filter((n) => n !== prevId);
    const pool = opts.length ? opts : nbr;
    return pool[Math.floor(rnd() * pool.length)];
  }

  /** Caminho mínimo (Dijkstra) de start→goal. Retorna lista de nodeIds. */
  route(start, goal) {
    if (start === goal) return [goal];
    const N = this.nodes.length;
    const dist = new Array(N).fill(Infinity);
    const prev = new Array(N).fill(-1);
    const done = new Array(N).fill(false);
    dist[start] = 0;
    for (let it = 0; it < N; it++) {
      let u = -1, bd = Infinity;
      for (let i = 0; i < N; i++) if (!done[i] && dist[i] < bd) { bd = dist[i]; u = i; }
      if (u === -1) break;
      if (u === goal) break;
      done[u] = true;
      const nu = this.nodes[u];
      for (const v of nu.nbr) {
        const w = Math.hypot(this.nodes[v].x - nu.x, this.nodes[v].z - nu.z);
        if (dist[u] + w < dist[v]) { dist[v] = dist[u] + w; prev[v] = u; }
      }
    }
    const path = [];
    for (let at = goal; at !== -1; at = prev[at]) path.unshift(at);
    return path[0] === start ? path : [start, goal]; // fallback degenerado
  }
}
